import type { ApiUpdate } from '../../../api/types';
import type { ActionReturnType, GlobalState } from '../../types';
import { ManagementProgress } from '../../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  LANG_CACHE_NAME,
  LOCK_SCREEN_ANIMATION_DURATION_MS,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../../../config';
import { updateAppBadge } from '../../../util/appBadge';
import * as cacheApi from '../../../util/cacheApi';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { buildCollectionByKey } from '../../../util/iteratees';
import { translate } from '../../../util/langProvider';
import {
  ACCOUNT_SLOT, getAccountsInfo, getAccountSlotUrl, getFirstLoggedInAccountSlot,
} from '../../../util/multiaccount';
import { unsubscribe } from '../../../util/notifications';
import { clearEncryptedSession, encryptSession, forgetPasscode } from '../../../util/passcode';
import {
  isLoginRequested, parseInitialLocationHash, resetInitialLocationHash, resetLocationHash,
} from '../../../util/routing';
import {
  clearStoredSession,
  clearStoredSessionSecrets,
  loadStoredSession,
  storeSession,
} from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import {
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, PLATFORM_ENV,
} from '../../../util/windowEnvironment';
import {
  callApi, callApiLocal, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import { serializeGlobal } from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import {
  addUsers, clearGlobalForLockScreen, updateManagementProgress, updatePasscodeSettings,
} from '../../reducers';
import { selectChat, selectChatMessage } from '../../selectors';

// tbiz 对 pts<=5 的账号每次 getDifference 都从 0 重放，收过的消息会再次作为 newMessage 下发。
// 必须在分发前判断：apiUpdaters/messages 的处理器先于 apiUpdaters/chats 执行，
// chats 累加未读数时消息已经入库，新旧分不出来
function markReplayedMessage(global: GlobalState, update: ApiUpdate): ApiUpdate {
  if (update['@type'] !== 'newMessage') return update;

  // 不用「id ≤ 最后一条」：并发投递时 id 可能乱序到达，会误伤真正的新消息
  const isReplayed = Boolean(selectChatMessage(global, update.chatId, update.id))
    || update.id <= (selectChat(global, update.chatId)?.lastReadInboxMessageId || 0);

  return isReplayed ? { ...update, isReplayed } : update;
}

addActionHandler('initApi', async (global, actions): Promise<void> => {
  const initialLocationHash = parseInitialLocationHash();
  const searchParams = new URLSearchParams(window.location.search);
  const isNonProduction = process.env.NODE_ENV !== 'production';
  const isTest = isNonProduction && (
    searchParams.get('test') === '1' || initialLocationHash?.tgWebAuthTest === '1'
  );
  const accountIds = Object.values(getAccountsInfo())
    .filter((account) => Boolean(account.isTest) === isTest)
    .map(({ userId }) => userId);

  void setShouldEnableDebugLog(Boolean(global.settings.byKey.shouldCollectDebugLogs));

  let sessionData;
  try {
    sessionData = await loadStoredSession();
  } catch {
    actions.showNotification({
      message: translate('NotificationSessionRestoreFailed'), tabId: getCurrentTabId(),
    });
    return;
  }
  global = getGlobal();

  void initApi((update) => {
    global = getGlobal();
    actions.apiUpdate(markReplayedMessage(global, update));
  }, {
    userAgent: navigator.userAgent,
    platform: PLATFORM_ENV,
    sessionData,
    isTest,
    accountIds,
    isWebmSupported: IS_WEBM_SUPPORTED,
    maxBufferSize: MAX_BUFFER_SIZE,
    webAuthToken: initialLocationHash?.tgWebAuthToken,
    dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined,
    mockScenario: isNonProduction ? initialLocationHash?.mockScenario : undefined,
    shouldAllowHttpTransport: false,
    shouldForceHttpTransport: false,
    shouldDebugExportedSenders: global.settings.byKey.shouldDebugExportedSenders,
    initialAuthMethod: isLoginRequested() ? 'phoneNumber' : undefined,
    langCode: global.settings.byKey.language,
  });
});

addActionHandler('setAuthPhoneNumber', (global, actions, payload): ActionReturnType => {
  const { phoneNumber, captchaValidate } = payload!;

  const preparedPhoneNumber = phoneNumber.includes('@')
    ? phoneNumber
    : phoneNumber.replace(/[^\d]/g, '');

  void callApi('provideAuthPhoneNumber', preparedPhoneNumber, captchaValidate);

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('setAuthCode', (global, actions, payload): ActionReturnType => {
  const { code } = payload!;

  void callApi('provideAuthCode', code);

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('setAuthPassword', (global, actions, payload): ActionReturnType => {
  const { password } = payload!;

  void callApi('provideAuthPassword', password);

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('requestAuthPasswordReset', (global): ActionReturnType => {
  void callApi('requestAuthPasswordReset');

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('uploadProfilePhoto', async (global, actions, payload): Promise<void> => {
  const {
    file, isFallback, isVideo, videoTs, bot,
    tabId = getCurrentTabId(),
  } = payload!;

  global = updateManagementProgress(global, ManagementProgress.InProgress, tabId);
  setGlobal(global);

  const result = await callApi('uploadProfilePhoto', file, isFallback, isVideo, videoTs, bot);
  if (!result) return;

  global = getGlobal();
  global = addUsers(global, buildCollectionByKey(result.users, 'id'));
  global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
  setGlobal(global);

  actions.loadFullUser({ userId: global.currentUserId! });
});

addActionHandler('signUp', (global, actions, payload): ActionReturnType => {
  const { firstName, inviteCode } = payload!;

  void callApi('provideAuthRegistration', { firstName, inviteCode });

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  void callApi('restartAuth');

  return {
    ...global,
    authLoginMethod: 'phone',
    authError: undefined,
  };
});

addActionHandler('returnToAuthEmailPhoneNumber', (global): ActionReturnType => {
  void callApi('restartAuth');

  return {
    ...global,
    authLoginMethod: 'email',
    authError: undefined,
  };
});

addActionHandler('goToAuthQrCode', (global): ActionReturnType => {
  void callApi('restartAuthWithQr');

  return {
    ...global,
    authIsLoadingQrCode: true,
    authError: undefined,
  };
});

addActionHandler('saveSession', (global, actions, payload): ActionReturnType => {
  if (global.passcode.isScreenLocked) {
    return;
  }

  const { sessionData } = payload;
  if (sessionData && global.authRememberMe) {
    if (global.passcode.hasPasscode) {
      storeSession(sessionData, global.currentUserId, { shouldPersistSessionSecrets: false });
      void encryptSession(JSON.stringify({ ...sessionData, userId: global.currentUserId }), undefined)
        .then(() => clearStoredSessionSecrets())
        .catch(() => clearStoredSession());
      return;
    }

    storeSession(sessionData, global.currentUserId);
  } else if (!sessionData) {
    clearStoredSession();
  } else {
    clearStoredSession();
  }
});

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  try {
    resetInitialLocationHash();
    resetLocationHash();
    await unsubscribe();
    await callApi('destroy');
    await forceWebsync(false);
  } catch (err) {
    // Do nothing
  }

  actions.reset();

  const accounts = getAccountsInfo();
  const targetAccountSlot = getFirstLoggedInAccountSlot(accounts) || 1;
  if (targetAccountSlot !== (ACCOUNT_SLOT || 1)) {
    window.location.replace(getAccountSlotUrl(targetAccountSlot, false, accounts[targetAccountSlot]?.isTest));
    return;
  }

  if (payload?.forceInitApi) {
    actions.initApi();
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', (global, actions): ActionReturnType => {
  clearStoredSession();
  clearEncryptedSession();

  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

addActionHandler('loadNearestCountry', async (global): Promise<void> => {
  if (global.connectionState !== 'connectionStateReady') {
    return;
  }

  const authNearestCountry = await callApi('fetchNearestCountry');

  global = getGlobal();
  global = {
    ...global,
    authNearestCountry,
  };
  setGlobal(global);
});

addActionHandler('setDeviceToken', (global, actions, deviceToken): ActionReturnType => {
  return {
    ...global,
    push: {
      deviceToken,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});

addActionHandler('lockScreen', async (global): Promise<void> => {
  const storedSession = await loadStoredSession();
  const sessionJson = JSON.stringify({ ...storedSession, userId: global.currentUserId });
  const globalJson = await serializeGlobal(global);

  await encryptSession(sessionJson, globalJson);
  forgetPasscode();
  clearStoredSession(undefined, true);
  updateAppBadge(0);

  global = getGlobal();
  global = updatePasscodeSettings(
    global,
    {
      isScreenLocked: true,
      invalidAttemptsCount: 0,
      timeoutUntil: undefined,
    },
  );
  setGlobal(global);

  setTimeout(() => {
    global = getGlobal();
    if (!global.passcode.isScreenLocked) {
      return;
    }
    global = clearGlobalForLockScreen(global);
    setGlobal(global);
  }, LOCK_SCREEN_ANIMATION_DURATION_MS);

  try {
    await unsubscribe();
    await callApi('destroy', true);
  } catch (err) {
    // Do nothing
  }
});
